import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';

import { isStripePaymentRequired } from '../../utils/featureFlags';
import { ORDER_QUERY } from '../../queries';
import LoadingSpinner from '../LoadingSpinner';
import ErrorText from '../ErrorText';
import { capitalize } from '../../utils';
import ThemeLink from '../ThemeLink';
import Button from '../inputs/Button';
import orderSummaryImage from '../../assets/completed-steps-animate.svg';
import InvoiceReceiptLink from './InvoiceReceiptLink';
import {
  initialState,
  reducer,
  CLEAR_SUCCESS,
  SET_SUCCESS,
} from '../../reducers/actionReducer';
import ToastNotification from '../ToastNotification';
import { useEffect, useReducer } from 'react';

export default function OrderSummaryPage({
  orderType,
  OrderLabel,
  OrderDetails,
  DownloadButton,
  title = 'Success!',
  description = "We've received your order. See below for a detailed summary.",
  hideOrderId = false,
  hideThankYou = false,
  // Optional element rendered above the title — e.g. the "profile report is
  // generating" notice after a Complete and Profile order.
  banner = null,
}) {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const { loading, error, data } = useQuery(ORDER_QUERY, {
    variables: { orderId, expandInvoice: orderType === 'audience' },
  });

  const order = data?.order;

  const [actionState, dispatchAction] = useReducer(reducer, initialState);

  useEffect(() => {
    if (actionState.success) {
      setTimeout(
        () =>
          dispatchAction({
            type: CLEAR_SUCCESS,
          }),
        5000
      );
    }
  }, [actionState.success]);

  return (
    <>
      <div className="w-full h-full flex flex-col overflow-auto py-10 px-12">
        {loading && <LoadingSpinner message="Getting your order details..." />}
        {error && <ErrorText message={error.message} />}
        {!loading && !error && data && (
          <div className="flex flex-row">
            <div className="flex flex-col flex-1 max-w-4xl">
              {banner}
              <h2 className="mb-2 font-bold text-3xl">{title}</h2>
              <div className="flex flex-row justify-between items-start mb-10">
                <p className="text-lg">{description}</p>
                {order?.invoiceId && (
                  <InvoiceReceiptLink invoiceId={order.invoiceId} />
                )}
              </div>

              <div className="flex flex-col mb-10">
                <h4 className="mb-2 font-bold text-xl">Order Summary</h4>
                {OrderLabel && <OrderLabel order={order} />}
                {!hideOrderId && (
                  <p>
                    Order ID: <span className="font-bold">{order.id}</span>
                  </p>
                )}
              </div>

              <OrderDetails order={order} />

              <h4 className="mb-2 font-bold text-xl">Payment Information</h4>
              <div className="mb-14 max-w-md">
                <div className="flex flex-row justify-between">
                  <p>Order Total:</p>
                  <p>
                    $
                    {parseFloat(order.amount).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </div>
                {order.paymentMethod?.brand && order.paymentMethod?.last4 && (
                  <div className="flex flex-row justify-between">
                    <p>Credit Card:</p>
                    <p>
                      {`${capitalize(order.paymentMethod.brand)} | ${
                        order.paymentMethod.last4
                      }`}
                    </p>
                  </div>
                )}
                {isStripePaymentRequired() && order.promoCode && (
                  <div className="flex flex-row justify-between">
                    <p>Promo Code Applied:</p>
                    <p>
                      {order.promoCode}{' '}
                      {order.invoice?.discount
                        ? `(${order.invoice.discount.name})`
                        : null}
                    </p>
                  </div>
                )}
              </div>
              <div className="mb-14 space-y-2">
                <p className="text-lg">
                  You can access this order's downloads and a copy of
                  this receipt via the{' '}
                  <ThemeLink to="../../../mywork/orders">
                    My Work
                  </ThemeLink>
                  {' '}module.
                </p>
              </div>
              <div className="flex flex-row space-x-3 mb-14">
                <Button
                  type="button"
                  secondary
                  small
                  onClick={() => navigate('../../../mywork/orders')}
                >
                  Back
                </Button>
                <DownloadButton
                  audienceName={order?.audienceName}
                  packageId={order?.packageId}
                  insightFileName={order?.insightFileName}
                  onDownloadStarted={() =>
                    dispatchAction({
                      type: SET_SUCCESS,
                      payload: {
                        message:
                          'Download started! Check your downloads folder.',
                      },
                    })
                  }
                ></DownloadButton>
              </div>
            </div>
            <div className="flex flex-col justify-center flex-1">
              <img
                src={orderSummaryImage}
                alt="person on laptop"
                className="w-1/2 mx-auto"
              />
            </div>
          </div>
        )}
      </div>
      <ToastNotification
        message={actionState.success}
        success={true}
        show={actionState.success}
      />
    </>
  );
}
